import type { ReceiveCurrency } from '~/lib/quote';
import { pageHeadMeta } from '~/lib/seo';

/**
 * SEO landing pages for `/calculator/{pair}` (Webflow parity).
 *
 * Titles, descriptions, and H1s were pulled from live Webflow pages
 * (2026-07-17). MAD/NGN live at Webflow slugs `calculator-usd-mad` /
 * `calculator-usd-ngn`; we serve the clean `/calculator/usd-mad` and
 * `/calculator/usd-ngn` paths requested for this site.
 */

export type CalculatorPairSlug =
  | 'usd-bdt'
  | 'usd-egp'
  | 'usd-pkr'
  | 'usd-try'
  | 'usd-mad'
  | 'usd-ngn';

export interface CalculatorPairContent {
  slug: CalculatorPairSlug;
  source: 'USD';
  target: ReceiveCurrency;
  /** Footer / nav label, e.g. "USD to BDT". */
  label: string;
  title: string;
  description: string;
  /** Hero H1; may include `\n` for the send-money-home line break. */
  heroTitle: string;
  ogImage: string;
}

export const CALCULATOR_PAIRS: Record<CalculatorPairSlug, CalculatorPairContent> = {
  'usd-bdt': {
    slug: 'usd-bdt',
    source: 'USD',
    target: 'BDT',
    label: 'USD to BDT',
    title: 'USD to BDT: Convert US Dollars to Bangladeshi taka',
    description:
      'Convert USD to BDT with today’s exchange rate. Check the latest rate and instantly estimate how much Bangladeshi taka you’ll receive when converting US dollars.',
    heroTitle: 'Send USD to BDT\nfast & securely at the best rates',
    ogImage: '/images/home/nsave-og.jpg',
  },
  'usd-egp': {
    slug: 'usd-egp',
    source: 'USD',
    target: 'EGP',
    label: 'USD to EGP',
    title: 'USD to EGP: Convert US Dollars to Egyptian pounds',
    description:
      'Convert USD to EGP with today’s exchange rate. Check the latest rate and instantly estimate how much Egyptian pounds you’ll receive when converting US dollars.',
    heroTitle: 'Send USD to EGP\nfast & securely at the best rates',
    ogImage: '/images/home/nsave-og.jpg',
  },
  'usd-pkr': {
    slug: 'usd-pkr',
    source: 'USD',
    target: 'PKR',
    label: 'USD to PKR',
    title: 'USD to PKR: Convert US Dollars to Pakistani rupees',
    description:
      'Convert USD to PKR with today’s exchange rate. Check the latest rate and instantly estimate how much Pakistani rupees you’ll receive when converting US dollars.',
    heroTitle: 'Send USD to Pakistan Rupee (PKR)\nfast & secure at the best rates',
    ogImage: '/images/home/nsave-og.jpg',
  },
  'usd-try': {
    slug: 'usd-try',
    source: 'USD',
    target: 'TRY',
    label: 'USD to TRY',
    title: 'USD to TRY: Convert US Dollars to Turkish Liras',
    description:
      'Convert USD to TRY with today’s exchange rate. Check today’s exchange rate and instantly estimate how much Turkish lira you’ll receive when converting US dollars.',
    heroTitle: 'Send USD to TRY\nfast & securely at the best rates',
    ogImage: '/images/home/nsave-og.jpg',
  },
  'usd-mad': {
    slug: 'usd-mad',
    source: 'USD',
    target: 'MAD',
    label: 'USD to MAD',
    title: 'USD to MAD: Convert US Dollars to Moroccan dirham',
    description:
      'Convert USD to MAD with today’s exchange rate. Check the latest rate and instantly estimate how much Moroccan dirhams you’ll receive when converting US dollars.',
    heroTitle: 'Send USD to MAD\nfast & securely at the best rates',
    ogImage: '/images/home/nsave-og.jpg',
  },
  'usd-ngn': {
    slug: 'usd-ngn',
    source: 'USD',
    target: 'NGN',
    label: 'USD to NGN',
    title: 'USD to NGN Exchange Rate: Convert US Dollars to Naira',
    description:
      'Convert USD to NGN with today’s exchange rate. Check the latest rate and instantly estimate how much Nigerian naira you’ll receive when converting US dollars.',
    heroTitle: 'Send USD to NGN\nfast & securely at the best rates',
    ogImage: '/images/home/nsave-og.jpg',
  },
};

export const CALCULATOR_PAIR_SLUGS = Object.keys(CALCULATOR_PAIRS) as CalculatorPairSlug[];

export function getCalculatorPair(slug: string): CalculatorPairContent | undefined {
  return CALCULATOR_PAIRS[slug as CalculatorPairSlug];
}

export function calculatorPairHeadMeta(pair: CalculatorPairContent | undefined) {
  if (!pair) return { meta: [], links: [], styles: [], scripts: [] };
  return pageHeadMeta({
    title: pair.title,
    description: pair.description,
    path: `/calculator/${pair.slug}`,
    ogImage: pair.ogImage,
  });
}
